import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { tokenManager } from '../lib/api'
import { MapPin, ExternalLink, Filter, Building2, X, Plus } from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface VendorEvaluation {
  id: string
  vendor: {
    id: number
    name: string
    website: string
    headquarters_location: string
    employee_count: number
    is_small_business: boolean
    is_8a_certified: boolean
    cage_code: string
  }
  product: {
    id: number
    name: string
    product_type: string
    description: string
  } | null
  overall_score: number
  scorecard_scores: {
    technical: number
    manufacturing_readiness: number
    dod_partnership: number
    vc_metrics: number
  }
  meets_threshold: boolean
  strengths: string[]
  gaps: string[]
}

const VendorMarketplace: React.FC = () => {
  const { programId } = useParams<{ programId: string }>()
  const [vendors] = useState<VendorEvaluation[]>([])
  const [loading, setLoading] = useState(true)
  const [filterThreshold, setFilterThreshold] = useState(65)
  const [showFilters, setShowFilters] = useState(false)

  // Add Vendor Modal state
  const [showAddVendorModal, setShowAddVendorModal] = useState(false)
  const [vendorSearchQuery, setVendorSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchingVendors, setSearchingVendors] = useState(false)
  const [creatingVendor, setCreatingVendor] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newVendorData, setNewVendorData] = useState({
    vendor_name: '',
    website: '',
    product_name: '',
    product_description: ''
  })

  useEffect(() => {
    // The pathway-scoped vendor evaluation endpoints have been removed. Until a
    // replacement contracting UI lands, this page renders no evaluated vendors.
    setLoading(false)
  }, [programId, filterThreshold])

  // Search vendors by name
  useEffect(() => {
    const searchVendors = async () => {
      if (vendorSearchQuery.length < 2) {
        setSearchResults([])
        return
      }

      setSearchingVendors(true)
      try {
        const token = tokenManager.getAccessToken()
        if (!token) return

        const response = await fetch(
          `${API_BASE_URL}/api/vendors/search/?q=${encodeURIComponent(vendorSearchQuery)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        )

        if (response.ok) {
          const data = await response.json()
          setSearchResults(data)
        }
      } catch (err) {
        console.error('Error searching vendors:', err)
      } finally {
        setSearchingVendors(false)
      }
    }

    const debounceTimer = setTimeout(searchVendors, 300)
    return () => clearTimeout(debounceTimer)
  }, [vendorSearchQuery])

  const handleSelectExistingVendor = async (vendorId: number) => {
    setError(null)

    try {
      const token = tokenManager.getAccessToken()
      if (!token) return

      // Invite this existing vendor
      const response = await fetch(`${API_BASE_URL}/api/submissions/bulk-invite/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          program_id: parseInt(programId!),
          vendor_ids: [vendorId],
          notes: 'Added from vendor marketplace'
        })
      })

      if (response.ok) {
        // Refresh vendors list
        window.location.reload()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to add vendor')
      }
    } catch (err) {
      console.error('Error adding existing vendor:', err)
      setError('Failed to add vendor')
    }
  }

  const handleCreateNewVendor = async () => {
    if (!newVendorData.vendor_name || !newVendorData.website || !newVendorData.product_name) {
      setError('Please fill in all required fields')
      return
    }

    setCreatingVendor(true)
    setError(null)

    try {
      const token = tokenManager.getAccessToken()
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/submissions/create-vendor-and-invite/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          program_id: parseInt(programId!),
          vendor_name: newVendorData.vendor_name,
          website: newVendorData.website,
          product_name: newVendorData.product_name,
          product_description: newVendorData.product_description,
          notes: 'Created from vendor marketplace'
        })
      })

      if (response.ok) {
        // Refresh vendors list
        window.location.reload()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to create vendor')
      }
    } catch (err) {
      console.error('Error creating vendor:', err)
      setError('Failed to create vendor')
    } finally {
      setCreatingVendor(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    if (score >= 75) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    if (score >= 65) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading vendor marketplace...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Vendor Marketplace
          </h1>
          <p className="text-muted-foreground">
            Browse all {vendors.length} evaluated vendors for your program.
          </p>
        </div>
        <button
          onClick={() => setShowAddVendorModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Vendor</span>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <Filter className="w-4 h-4" />
          <span>Filters</span>
        </button>

        {showFilters && (
          <div className="mt-4 bg-card rounded-lg border border-border p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-card-foreground">
                Minimum Score:
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={filterThreshold}
                onChange={(e) => setFilterThreshold(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-semibold text-foreground w-12">
                {filterThreshold}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Vendor Grid */}
      {vendors.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <p className="text-muted-foreground">
            No vendors found matching your criteria.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((evaluation) => (
            <div
              key={evaluation.vendor.id}
              className="bg-card rounded-lg border border-border p-4 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md transition-all"
            >
              {/* Vendor Info */}
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {evaluation.vendor.name}
                </h3>
                {evaluation.vendor.headquarters_location && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                    <MapPin className="w-3 h-3" />
                    <span>{evaluation.vendor.headquarters_location}</span>
                  </div>
                )}
                {evaluation.vendor.website && (
                  <a
                    href={evaluation.vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <span>Visit website</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Overall Score */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(evaluation.overall_score)}`}>
                  {Math.round(evaluation.overall_score)}/100
                </span>
                {evaluation.vendor.is_small_business && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-xs font-medium">
                    SB
                  </span>
                )}
                {evaluation.vendor.is_8a_certified && (
                  <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded text-xs font-medium">
                    8(a)
                  </span>
                )}
              </div>

              {/* Product */}
              {evaluation.product && (
                <div className="mb-3 p-2 bg-background rounded">
                  <p className="text-xs font-semibold text-card-foreground mb-1">
                    Product: {evaluation.product.name}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {evaluation.product.description}
                  </p>
                </div>
              )}

              {/* Scorecard Breakdown */}
              <div className="space-y-1.5 mb-3">
                {Object.entries(evaluation.scorecard_scores).map(([key, score]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs text-muted-foreground mb-0.5">
                        <span className="capitalize">{key.replace('_', ' ')}</span>
                        <span>{Math.round(score)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 dark:bg-blue-500 rounded-full"
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Strengths & Gaps */}
              <div className="border-t border-border pt-3">
                {evaluation.strengths.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-semibold text-green-700 dark:text-green-300 mb-1">
                      Strengths:
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {evaluation.strengths.slice(0, 2).map((strength, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-success">✓</span>
                          <span className="flex-1 line-clamp-2">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {evaluation.gaps.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-1">
                      Gaps:
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {evaluation.gaps.slice(0, 1).map((gap, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-orange-600 dark:text-orange-400">!</span>
                          <span className="flex-1 line-clamp-2">{gap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">
                Add Vendor
              </h3>
              <button
                onClick={() => {
                  setShowAddVendorModal(false)
                  setShowCreateForm(false)
                  setVendorSearchQuery('')
                  setNewVendorData({ vendor_name: '', website: '', product_name: '', product_description: '' })
                  setError(null)
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {!showCreateForm ? (
                <>
                  {/* Search Existing Vendors */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Search for existing vendor
                    </label>
                    <input
                      type="text"
                      value={vendorSearchQuery}
                      onChange={(e) => setVendorSearchQuery(e.target.value)}
                      placeholder="Type vendor name..."
                      className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Search Results */}
                  {searchingVendors && (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    </div>
                  )}

                  {!searchingVendors && searchResults.length > 0 && (
                    <div className="space-y-2 mb-6">
                      <p className="text-sm font-medium text-card-foreground mb-2">
                        Found {searchResults.length} vendor{searchResults.length !== 1 ? 's' : ''}:
                      </p>
                      {searchResults.map((vendor) => (
                        <div
                          key={vendor.id}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600"
                        >
                          <div>
                            <p className="font-medium text-foreground">{vendor.name}</p>
                            {vendor.headquarters_location && (
                              <p className="text-sm text-muted-foreground">{vendor.headquarters_location}</p>
                            )}
                            {vendor.website && (
                              <p className="text-xs text-primary">{vendor.website}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleSelectExistingVendor(vendor.id)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!searchingVendors && vendorSearchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className="text-center py-4 mb-6 text-muted-foreground">
                      No vendors found matching "{vendorSearchQuery}"
                    </div>
                  )}

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-card text-muted-foreground">or</span>
                    </div>
                  </div>

                  {/* Create New Vendor Button */}
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full py-3 border-2 border-dashed border-input rounded-lg hover:border-blue-400 dark:hover:border-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-card-foreground font-medium flex items-center justify-center gap-2"
                  >
                    <Building2 className="w-5 h-5" />
                    <span>Create New Vendor</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Create New Vendor Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Vendor Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newVendorData.vendor_name}
                        onChange={(e) => setNewVendorData({ ...newVendorData, vendor_name: e.target.value })}
                        placeholder="e.g., Acme Defense Systems"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Website <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={newVendorData.website}
                        onChange={(e) => setNewVendorData({ ...newVendorData, website: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newVendorData.product_name}
                        onChange={(e) => setNewVendorData({ ...newVendorData, product_name: e.target.value })}
                        placeholder="e.g., Eagle-X Drone System"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Product Description
                      </label>
                      <textarea
                        value={newVendorData.product_description}
                        onChange={(e) => setNewVendorData({ ...newVendorData, product_description: e.target.value })}
                        placeholder="Brief description of the product..."
                        rows={3}
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="mt-4 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    ← Back to search
                  </button>
                </>
              )}

              {error && (
                <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {showCreateForm && (
              <div className="border-t border-border p-4 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAddVendorModal(false)
                    setShowCreateForm(false)
                    setNewVendorData({ vendor_name: '', website: '', product_name: '', product_description: '' })
                    setError(null)
                  }}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-foreground rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewVendor}
                  disabled={creatingVendor || !newVendorData.vendor_name || !newVendorData.website || !newVendorData.product_name}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creatingVendor ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    'Create Vendor'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorMarketplace

